import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MapPin, Check, X, AlertTriangle, Info } from 'lucide-react';
import toast from 'react-hot-toast';
import Layout from '../../components/layout/AppLayout';
import Header from '../../../shared/components/layout/Header';
import { useSmartBack } from '../../../shared/navigation';
import Loader from '../../../shared/components/ui/Loader';
import PunchLocationModal from '../../../shared/components/ui/PunchLocationModal';
import {
  getPendingLabourAttendance,
  decideLabourAttendance,
  getPendingLabourOvertime,
  decideLabourOvertime
} from '../../services/api/implementation/impl';
import { Callout } from '../../../shared/components/ui/primitives';

/** Beyond this the punch is worth a second look. Matches the server default. */
const DEFAULT_GEOFENCE_RADIUS_M = 500;

/**
 * Attendance awaiting an engineer's decision.
 *
 * Contractor-marked rows always land here. In-house rows do too, but only
 * when the builder has turned on in-house muster approval — default off, so a
 * supervisor's own marking still accrues on confirm.
 *
 * Approving accrues the day's wage and rejecting removes it, so this queue is
 * not paperwork: an unattended queue means people are not being paid.
 */
const MusterApprovals = () => {
  const { onBack } = useSmartBack({ fallback: '/builder/workforce?tab=labour' });
  const queryClient = useQueryClient();
  const [mapRow, setMapRow] = useState(null);

  const { data, isFetching } = useQuery({
    queryKey: ['labourAttendancePending'],
    queryFn: () => getPendingLabourAttendance(200)
  });
  const overtime = useQuery({
    queryKey: ['labourOvertimePending'],
    queryFn: () => getPendingLabourOvertime(200)
  });

  const decide = useMutation({
    mutationFn: ({ id, approve }) => decideLabourAttendance(id, approve),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['labourAttendancePending'] });
      queryClient.invalidateQueries({ queryKey: ['labourOvertimePending'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      setMapRow(null);
      toast.success(variables.approve ? 'Attendance approved' : 'Attendance rejected');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  const decideOt = useMutation({
    mutationFn: ({ id, approve }) => decideLabourOvertime(id, approve),
    onSuccess: (_res, variables) => {
      queryClient.invalidateQueries({ queryKey: ['labourOvertimePending'] });
      queryClient.invalidateQueries({ queryKey: ['labourDues'] });
      toast.success(variables.approve ? 'Overtime released' : 'Overtime declined');
    },
    onError: (err) =>
      toast.error(err?.response?.data?.message || 'Could not record the decision')
  });

  const rows = data?.data ?? [];
  const otRows = overtime.data?.data ?? [];

  return (
    <Layout>
      <Header backArrow onBack={onBack} data={{ name: 'Attendance Approvals' }} />

      <div className="p-4 space-y-4">
        <Callout tone="info" collapsible icon={<Info size={14} />} title="About attendance approvals" className="sm:max-w-2xl">
          Contractor-marked days always wait here; in-house days only if that rule is on. Approving adds the day&apos;s wage; rejecting removes it.
        </Callout>

        {isFetching ? (
          <Loader />
        ) : rows.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-10 text-center text-gray-500">
            Nothing waiting for approval.
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <Th>Workman</Th>
                  <Th>Date</Th>
                  <Th>Hours</Th>
                  <Th>Marked</Th>
                  <Th>Location</Th>
                  <Th>Decision</Th>
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <Td>
                      <div className="font-medium text-gray-900">
                        {row.worker?.name ?? `#${row.worker_id}`}
                      </div>
                      <div className="text-xs text-gray-500">
                        {row.worker?.skill_level ?? ''}
                      </div>
                    </Td>
                    <Td>{formatDate(row.date)}</Td>
                    <Td>
                      {Number(row.workingHours ?? 0)}
                      {Number(row.extraWorkingHours) > 0 && (
                        <span className="text-info-fg">
                          {' '}
                          +{Number(row.extraWorkingHours)} OT
                        </span>
                      )}
                      <div className="text-xs text-gray-400">
                        {row.hours_source === 'PUNCH' ? 'from punches' : 'declared'}
                      </div>
                    </Td>
                    <Td>
                      <div className="text-xs text-gray-600">
                        {sourceLabel(row.marked_source)}
                      </div>
                    </Td>
                    <Td>
                      <LocationCell row={row} onOpen={() => setMapRow(row)} />
                    </Td>
                    <Td>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: row.id, approve: true })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded bg-success text-white text-xs font-medium disabled:opacity-50"
                        >
                          <Check size={13} /> Approve
                        </button>
                        <button
                          type="button"
                          disabled={decide.isPending}
                          onClick={() => decide.mutate({ id: row.id, approve: false })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded border border-danger-border text-danger-fg text-xs font-medium disabled:opacity-50"
                        >
                          <X size={13} /> Reject
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <PunchLocationModal
          open={!!mapRow}
          onClose={() => setMapRow(null)}
          title={mapRow?.worker?.name ?? (mapRow ? `#${mapRow.worker_id}` : '')}
          subtitle={joinMeta(mapRow?.project?.name, formatDate(mapRow?.date))}
          place={mapRow ? placeLabel(mapRow) : null}
          eventLabel={
            mapRow?.check_in
              ? `Marked from · ${formatTime(mapRow.check_in)}`
              : 'Marked from'
          }
          accuracyM={mapRow?.punch_in_accuracy_m}
          lat={mapRow?.punch_in_lat}
          lng={mapRow?.punch_in_lng}
          points={
            mapRow
              ? [
                  {
                    lat: mapRow.punch_in_lat,
                    lng: mapRow.punch_in_lng,
                    accuracy_m: mapRow.punch_in_accuracy_m,
                    distance_m: mapRow.punch_in_distance_m,
                    label: 'Marked from'
                  }
                ]
              : []
          }
          fence={mapRow ? fenceFor(mapRow) : null}
          overlay={mapRow?.project?.name}
          caption="This pin is the supervisor's phone, not the workman's. Labour do not carry a device."
        />

        <h2 className="text-sm font-semibold text-gray-800 pt-4">Pending overtime</h2>
        <p className="text-xs text-gray-500">
          Only used when overtime pre-approval is on. Releasing adds the overtime to the wages.
        </p>
        {overtime.isFetching ? (
          <Loader />
        ) : otRows.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-lg py-8 text-center text-gray-500">
            No overtime waiting to be released.
          </div>
        ) : (
          <div className="overflow-x-auto bg-white rounded-lg border border-gray-200">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <Th>Workman</Th>
                  <Th>Date</Th>
                  <Th>OT hours</Th>
                  <Th>Amount</Th>
                  <Th>Decision</Th>
                </tr>
              </thead>
              <tbody>
                {otRows.map((row) => (
                  <tr key={row.id} className="border-t border-gray-100">
                    <Td>
                      <div className="font-medium text-gray-900">
                        {row.worker?.name ?? `#${row.worker_id}`}
                      </div>
                    </Td>
                    <Td>{formatDate(row.date)}</Td>
                    <Td>{Number(row.pending_ot_hours ?? 0)}</Td>
                    <Td>₹{Number(row.pending_ot_amount ?? 0)}</Td>
                    <Td>
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={decideOt.isPending}
                          onClick={() => decideOt.mutate({ id: row.id, approve: true })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded bg-success text-white text-xs font-medium disabled:opacity-50"
                        >
                          <Check size={13} /> Release
                        </button>
                        <button
                          type="button"
                          disabled={decideOt.isPending}
                          onClick={() => decideOt.mutate({ id: row.id, approve: false })}
                          className="flex items-center gap-1 px-3 py-1.5 rounded border border-danger-border text-danger-fg text-xs font-medium disabled:opacity-50"
                        >
                          <X size={13} /> Decline
                        </button>
                      </div>
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
};

/**
 * The coordinate belongs to the supervisor or contractor who marked the day,
 * not the workman. Show the site name, not raw lat/lng, and keep that
 * distinction on the map so it is not read as proof the workman was there.
 */
const LocationCell = ({ row, onOpen }) => {
  if (row.location_source === 'LEGACY_APP') {
    return (
      <span className="text-xs text-gray-500" title="Marked from an app build without location support">
        No location (old app)
      </span>
    );
  }
  if (row.location_source === 'PORTAL') {
    return <span className="text-xs text-gray-500">Entered from portal</span>;
  }
  if (row.punch_in_lat == null) {
    return <span className="text-xs text-gray-400">—</span>;
  }

  const distance = row.punch_in_distance_m;
  const outOfRange = distance != null && distance > DEFAULT_GEOFENCE_RADIUS_M;
  const place = placeLabel(row);

  return (
    <button type="button" onClick={onOpen} className="text-left text-xs max-w-[220px]">
      <div className="flex items-start gap-1 text-brand-700 hover:underline font-medium">
        <MapPin size={12} className="mt-0.5 shrink-0" />
        <span>{place}</span>
      </div>
      {row.punch_in_accuracy_m != null && (
        <div className="text-gray-400">±{row.punch_in_accuracy_m}m accuracy</div>
      )}
      {distance != null && (
        <div className={outOfRange ? 'text-warning-fg flex items-center gap-1' : 'text-gray-400'}>
          {outOfRange && <AlertTriangle size={11} />}
          {formatDistance(distance)} from site
        </div>
      )}
      <div className="text-gray-400 italic">supervisor&apos;s phone</div>
      <div className="text-gray-400">View map</div>
    </button>
  );
};

function placeLabel(row) {
  if (row.project?.name) return row.project.name;
  return 'Marked from app';
}

function fenceFor(row) {
  if (row.project?.site_lat == null) return null;
  return {
    lat: row.project.site_lat,
    lng: row.project.site_lng,
    radius_m: row.project.geofence_radius_m || DEFAULT_GEOFENCE_RADIUS_M,
    name: row.project.name
  };
}

function sourceLabel(source) {
  switch (source) {
    case 'CONTRACTOR_APP':
      return 'Contractor app';
    case 'SITE_ENGINEER_APP':
      return 'Site engineer app';
    case 'PORTAL':
      return 'Portal';
    case 'IMPORT':
      return 'Import';
    default:
      return '—';
  }
}

function formatDistance(metres) {
  if (metres < 1000) return `${metres}m`;
  return `${(metres / 1000).toFixed(1)}km`;
}

function formatDate(value) {
  if (!value) return '—';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', {
    day: '2-digit',
    month: 'short',
    year: 'numeric'
  });
}

function formatTime(value) {
  if (!value) return '';
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString('en-IN', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

function joinMeta(...parts) {
  return parts.filter(Boolean).join(' · ') || null;
}

const Th = ({ children }) => (
  <th className="text-left font-medium px-4 py-3 whitespace-nowrap">{children}</th>
);
const Td = ({ children }) => <td className="px-4 py-3 align-top">{children}</td>;

export default MusterApprovals;
